import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { notification } from "antd";
import CouponService from "services/CouponService";
export const initialState = {
  data: [],
  dataDetail: [],
  dataGroup: null,
  dataTemplate: null,
  loading: false,
  error: null,
  redirect: false,
};

const getGroupChildren = (coupons, groupId, groupName) => {
  const numericId = Number(groupId);

  const hasGroupedApiShape = coupons.some(
    (coupon) =>
      Array.isArray(coupon.codes) ||
      Array.isArray(coupon.coupon_codes) ||
      (Array.isArray(coupon.children) &&
        coupon.children.some((child) => child.code))
  );

  if (hasGroupedApiShape) {
    const group = coupons.find((item) => {
      const children = item.codes || item.coupon_codes || item.children || [];
      return (
        Number(item.id) === numericId ||
        children.some((child) => Number(child.id) === numericId)
      );
    });

    if (!group) return [];

    return (group.codes || group.coupon_codes || group.children || []).map(
      (child) => ({
        ...child,
        name: child.name || group.name,
      })
    );
  }

  return coupons.filter((coupon) => coupon.name === groupName);
};

export const fetchCouponData = createAsyncThunk(
  "coupon/fetchCouponData",
  async (data, { rejectWithValue }) => {
    try {
      const { page, pageSize, sort, searchText, state } = data;
      const response = await CouponService.get({
        page,
        pageSize,
        sort,
        searchText,
        state,
        isPublic: 1,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const fetchCouponDetail = createAsyncThunk(
  "coupon/fetchCouponDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await CouponService.getById(id);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const createCouponData = createAsyncThunk(
  "coupon/createCouponData",
  async (data, { rejectWithValue }) => {
    try {
      const { id, name, code, price, discount, discount_type, is_public } = data;
      const response = await CouponService.createCoupon({
        id,
        name,
        code,
        price,
        discount,
        discount_type,
        is_public,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const createPublicCouponGroupData = createAsyncThunk(
  "coupon/createPublicCouponGroupData",
  async (data, { rejectWithValue }) => {
    try {
      const { name, children = [] } = data;
      if (!children.length) {
        return rejectWithValue({ message: "At least one coupon code is required" });
      }

      const [first, ...rest] = children;
      const created = await CouponService.createCoupon({
        name,
        code: first.code,
        price: 0,
        discount: first.discount,
        discount_type: first.discount_type,
        is_public: "1",
      });

      const createdData = created?.data;
      const firstId = createdData?.id ?? createdData?.data?.id;

      if (rest.length > 0) {
        if (!firstId) {
          return rejectWithValue({
            message: "Failed to resolve created coupon id for group add",
          });
        }

        await CouponService.updateCouponGroup(firstId, {
          add: rest.map((child) => ({
            code: child.code,
            price: 0,
            is_public: true,
            discount: child.discount,
            discount_type: child.discount_type,
          })),
        });
      }

      return created;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const updateCouponData = createAsyncThunk(
  "coupon/updateCouponData",
  async (data, { rejectWithValue }) => {
    try {
      const { id, name, code, price, discount, discount_type, is_public } = data;
      const response = await CouponService.updateCoupon({
        id,
        name,
        code,
        price,
        discount,
        discount_type,
        is_public,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const patchCouponData = createAsyncThunk(
  "coupon/patchCouponData",
  async (data, { rejectWithValue }) => {
    try {
      const { id, discount, discount_type } = data;
      const response = await CouponService.patchCoupon(id, {
        discount,
        discount_type,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const deleteCouponData = createAsyncThunk(
  "coupon/deleteCouponData",
  async (id, { rejectWithValue }) => {
    try {
      const response = await CouponService.deleteCoupon(id);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const fetchCouponGroupData = createAsyncThunk(
  "coupon/fetchCouponGroupData",
  async (groupId, { rejectWithValue }) => {
    try {
      const detailResponse = await CouponService.getById(groupId);
      const seed = detailResponse.data;

      if (!seed) {
        return rejectWithValue({ status: 404, message: "Coupon group not found" });
      }

      const listResponse = await CouponService.get({
        page: 1,
        pageSize: 10000,
        searchText: seed.name,
        isPublic: seed.is_public ? 1 : 0,
      });
      const raw = listResponse.data;
      const coupons = Array.isArray(raw) ? raw : raw?.data || [];
      const children = getGroupChildren(coupons, groupId, seed.name);

      if (!children.length) {
        return rejectWithValue({ status: 404, message: "Coupon group not found" });
      }

      return {
        id: Number(groupId),
        name: seed.name,
        children,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const updateCouponGroupData = createAsyncThunk(
  "coupon/updateCouponGroupData",
  async (data, { rejectWithValue }) => {
    try {
      const { id, name, add, remove } = data;
      const body = {};

      if (name !== undefined && name !== null && name !== "") {
        body.name = name;
      }
      if (Array.isArray(add) && add.length > 0) {
        body.add = add;
      }
      if (Array.isArray(remove) && remove.length > 0) {
        body.remove = remove;
      }

      const response = await CouponService.updateCouponGroup(id, body);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const deleteCouponGroupData = createAsyncThunk(
  "coupon/deleteCouponGroupData",
  async (groupId, { rejectWithValue }) => {
    try {
      const response = await CouponService.deleteCouponGroup(groupId);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const fetchCouponTemplate = createAsyncThunk(
  "coupon/fetchCouponTemplate",
  async (_, { rejectWithValue }) => {
    try {
      const response = await CouponService.getTemplate();
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const createBulkCouponData = createAsyncThunk(
  "coupon/createBulkCouponData",
  async (data, { rejectWithValue }) => {
    try {
      const response = await CouponService.createBulkCoupon(data);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const couponSlice = createSlice({
  name: "coupon",
  initialState,
  reducers: {
    showLoading: (state) => {
      state.loading = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCouponData.pending, (state) => {
        state.loading = true;
        state.redirect = false;
      })
      .addCase(fetchCouponData.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = false;
        state.data = action.payload.data;
      })
      .addCase(fetchCouponData.rejected, (state, action) => {
        state.loading = false;
        state.redirect = false;
        state.error = action.error.message;
      })
      .addCase(fetchCouponDetail.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCouponDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.dataDetail = action.payload.data;
        state.error = null;
      })
      .addCase(fetchCouponDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createCouponData.pending, (state) => {
        state.loading = true;
        state.redirect = false;
      })
      .addCase(createCouponData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.redirect = true;
        notification.success({ message: "Proses Berhasil" });
      })
      .addCase(createCouponData.rejected, (state, action) => {
        state.redirect = false;
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createPublicCouponGroupData.pending, (state) => {
        state.loading = true;
        state.redirect = false;
      })
      .addCase(createPublicCouponGroupData.fulfilled, (state) => {
        state.loading = false;
        state.redirect = true;
        notification.success({ message: "Proses Berhasil" });
      })
      .addCase(createPublicCouponGroupData.rejected, (state, action) => {
        state.redirect = false;
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateCouponData.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCouponData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        notification.success({ message: "Proses Berhasil" });
      })
      .addCase(updateCouponData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(patchCouponData.pending, (state) => {
        state.loading = true;
        state.redirect = false;
      })
      .addCase(patchCouponData.fulfilled, (state, action) => {
        state.loading = false;
        state.dataDetail = action.payload.data;
        state.redirect = true;
        notification.success({ message: "Proses Berhasil" });
      })
      .addCase(patchCouponData.rejected, (state, action) => {
        state.redirect = false;
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteCouponData.pending, (state) => {
        state.redirect = false;
        state.loading = true;
      })
      .addCase(deleteCouponData.fulfilled, (state) => {
        state.loading = false;
        state.redirect = true;
        notification.success({ message: "Proses Berhasil" });
      })
      .addCase(deleteCouponData.rejected, (state, action) => {
        state.redirect = false;
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCouponGroupData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCouponGroupData.fulfilled, (state, action) => {
        state.loading = false;
        state.dataGroup = action.payload;
        state.error = null;
      })
      .addCase(fetchCouponGroupData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateCouponGroupData.pending, (state) => {
        state.loading = true;
        state.redirect = false;
      })
      .addCase(updateCouponGroupData.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = true;
        if (action.payload?.data) {
          state.dataGroup = action.payload.data;
        }
        notification.success({ message: "Proses Berhasil" });
      })
      .addCase(updateCouponGroupData.rejected, (state, action) => {
        state.redirect = false;
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteCouponGroupData.pending, (state) => {
        state.redirect = false;
        state.loading = true;
      })
      .addCase(deleteCouponGroupData.fulfilled, (state) => {
        state.loading = false;
        state.redirect = true;
        notification.success({ message: "Proses Berhasil" });
      })
      .addCase(deleteCouponGroupData.rejected, (state, action) => {
        state.redirect = false;
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCouponTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCouponTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.dataTemplate = action.payload;
      })
      .addCase(fetchCouponTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createBulkCouponData.pending, (state) => {
        state.loading = true;
        state.redirect = false;
      })
      .addCase(createBulkCouponData.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = true;
        notification.success({ message: "Proses Berhasil" });
      })
      .addCase(createBulkCouponData.rejected, (state, action) => {
        state.redirect = false;
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { showLoading } = couponSlice.actions;

export default couponSlice.reducer;
